// Chat/shared/watchToken.ts
//
// The companion's "Watch again" token (ORA-1055). When an answer about a
// moment in the course video can point at the stretch that explains it, the
// model ends with `Watch again: [[watch 0:38-0:57]]`; the renderer draws the
// token as a chip that seeks the course video there.
//
// Accepted: `[[watch m:ss-m:ss]]`, an en or em dash in place of the hyphen,
// spaces around it, a single `[[watch m:ss]]`, and `h:mm:ss` times. Anything
// else in the brackets is not a token, and renders as the text it is.

/**
 * What the inline pass matches. Deliberately looser than the parser, so a
 * malformed token is one token the renderer prints verbatim rather than a
 * bracket the citation branch half-reads.
 */
export const WATCH_TOKEN_SOURCE = '\\[\\[[wW]atch\\b[^\\]\\n]{0,40}\\]\\]';

const CLOCK = '(?:\\d{1,2}:)?\\d{1,3}:[0-5]\\d';

const WATCH_TOKEN_RE = new RegExp(
  `^\\[\\[watch\\s+(${CLOCK})(?:\\s*[-–—]\\s*(${CLOCK}))?\\s*\\]\\]$`,
  'i'
);

export interface WatchRange {
  startSec: number;
  /** Null for a single moment (`[[watch 0:38]]`). */
  endSec: number | null;
}

const clockToSeconds = (clock: string): number =>
  clock
    .split(':')
    .map(Number)
    .reduce((total, part) => total * 60 + part, 0);

/** `m:ss`, the way the video UI prints every time (`64:20`, not `1:04:20`). */
export const formatWatchClock = (seconds: number): string => {
  const total = Math.max(0, Math.floor(seconds));

  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
};

/** The range a token names, or null when it is not a well-formed token. */
export const parseWatchToken = (token: string): WatchRange | null => {
  const match = WATCH_TOKEN_RE.exec(token.trim());
  if (!match) return null;

  const startSec = clockToSeconds(match[1]);
  const endSec = match[2] ? clockToSeconds(match[2]) : null;

  // A range that runs backwards is a typo the student cannot act on.
  if (endSec !== null && endSec < startSec) return null;

  return { startSec, endSec: endSec === startSec ? null : endSec };
};

/** `0:38–0:57`, or `0:38` for a single moment. */
export const formatWatchRange = (
  { startSec, endSec }: WatchRange,
  separator = '–'
): string =>
  endSec === null
    ? formatWatchClock(startSec)
    : `${formatWatchClock(startSec)}${separator}${formatWatchClock(endSec)}`;

/**
 * Every well-formed token as words — `0:38 to 0:57` — for read-aloud and
 * Copy, where brackets mean nothing. Malformed tokens are left as written.
 */
export const watchTokensToText = (text: string): string =>
  text.includes('[[')
    ? text.replace(new RegExp(WATCH_TOKEN_SOURCE, 'g'), (token) => {
        const range = parseWatchToken(token);
        return range ? formatWatchRange(range, ' to ') : token;
      })
    : text;
